import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";

const gramsOf = (serving) => {
  const m = (serving || "").match(/([\d.]+)\s*g/i);
  return m ? parseFloat(m[1]) : null;
};
const r1 = (n) => Math.round(n * 10) / 10;

export const scaleFood = (f, qty) => {
  const baseG = gramsOf(f.serving);
  const scale = baseG ? qty / baseG : qty;
  const label = baseG ? `${qty} g` : qty === 1 ? f.serving || "1 serving" : `${qty} × ${f.serving || "serving"}`;
  return {
    label,
    calories: f.calories != null ? r1(f.calories * scale) : 0,
    protein: f.protein != null ? r1(f.protein * scale) : 0,
    carbs: f.carbs != null ? r1(f.carbs * scale) : 0,
    fat: f.fat != null ? r1(f.fat * scale) : 0,
  };
};

export const FoodSearchPanel = ({ search, onAdd, placeholder = "Search any food — e.g. paneer, oats, chicken breast…", hint = "Powered by FatSecret — set your own portion (g or servings), macros adjust instantly." }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState({});
  const [adding, setAdding] = useState("");

  const run = async () => {
    if (q.trim().length < 2) { toast.error("Type at least 2 characters"); return; }
    setBusy(true);
    try {
      const res = await search(q.trim());
      setResults(res);
      const init = {};
      res.forEach((f) => { const g = gramsOf(f.serving); init[f.food_id] = g || 1; });
      setQty(init);
      if (res.length === 0) toast.info("No foods found — try another term");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Food search failed");
    } finally { setBusy(false); }
  };

  const add = async (f, scaled) => {
    setAdding(f.food_id);
    try {
      await onAdd({ name: f.name + (f.brand ? ` (${f.brand})` : ""), serving: scaled.label, food_id: f.food_id, calories: scaled.calories, protein: scaled.protein, carbs: scaled.carbs, fat: scaled.fat });
    } finally { setAdding(""); }
  };

  return (
    <div data-testid="food-search-panel">
      <div className="flex gap-2">
        <Input data-testid="food-search-input" placeholder={placeholder} value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} className="bg-[#F7F5F0] border-[#E5E2D9] h-10 text-sm" />
        <button data-testid="food-search-btn" onClick={run} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full px-5 bg-[#2D372B] text-white text-xs font-medium hover:bg-[#89937C] disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Search
        </button>
      </div>
      <p className="mt-1.5 text-[10px] text-[#9AA294]">{hint}</p>

      {results && results.length > 0 && (
        <div className="mt-3 max-h-72 overflow-y-auto space-y-1.5" data-testid="food-results">
          {results.map((f) => {
            const baseG = gramsOf(f.serving);
            const v = qty[f.food_id] ?? (baseG || 1);
            const scaled = scaleFood(f, v);
            return (
              <div key={f.food_id} className="border border-[#F0EEE6] rounded-xl px-3 py-2" data-testid="food-result-row">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-medium truncate">{f.name}{f.brand ? ` — ${f.brand}` : ""}</div>
                    <div className="text-[10px] text-[#9AA294]">Base: {f.serving ? `per ${f.serving}` : "per serving"} · {f.calories} kcal</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" step={baseG ? "5" : "0.25"} value={v} data-testid={`food-qty-${f.food_id}`}
                        onChange={(e) => setQty({ ...qty, [f.food_id]: parseFloat(e.target.value) || 0 })}
                        className="w-20 h-8 bg-[#F7F5F0] border border-[#E5E2D9] rounded-lg px-2 text-xs text-right" />
                      <span className="text-[10px] text-[#5C665A] w-14">{baseG ? "grams" : `× ${f.serving || "serving"}`}</span>
                    </div>
                    {onAdd && (
                      <button data-testid={`food-add-${f.food_id}`} onClick={() => add(f, scaled)} disabled={adding === f.food_id || !v}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-medium bg-[#C57B57] text-white hover:bg-[#AD6A48] disabled:opacity-60">
                        {adding === f.food_id ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-[11px] font-semibold text-[#5C665A]" data-testid={`food-scaled-${f.food_id}`}>
                  {scaled.label}: <span className="text-[#AD6A48]">{scaled.calories} kcal</span> · P {scaled.protein}g · C {scaled.carbs}g · F {scaled.fat}g
                </div>
              </div>
            );
          })}
        </div>
      )}
      {results && results.length === 0 && <p className="mt-3 text-xs text-[#9AA294]">No foods found.</p>}
    </div>
  );
};
